import { useEffect, useState } from 'react'

import { Form, Formik } from 'formik'
import { FormattedNumber } from 'react-intl'
import { styled } from 'styled-components'

import { type ContractDto } from '@b-stock/contract-api-client'
import { useDeviceInfo } from '@b-stock/fe-core/bstock-next/components'
import {
  type OfferingItem,
  type OrchestratedQuote,
} from '@b-stock/fe-core/bstock-next/queries'
import {
  Breakpoint,
  Button,
  CurrencyStringCents,
  ErrorSnackbar,
  FormattedMessage,
  showSnackbar,
} from '@b-stock/fe-core/bstock-react'
import { Breakpoints, Typography } from '@b-stock/fe-core/design-system'
import { designColors } from '@b-stock/fe-core/theme'
import type { OfferDto, OfferingDto } from '@b-stock/offering-api-client'

import { SHOW_CONTRACT_QUANTITIES } from '@/buyer-portal/helpers/constants'

import { OfferAllButton, OfferAllQuantitiesButton } from './atoms'
import { OfferingFormTable } from './OfferingFormTable'
import { OfferingFormTiles } from './OfferingFormTiles'
import { useOfferingForm } from './useOfferingForm'
import { useValidationSchema } from './useValidationSchema'
import { getRetailQuantityPrice } from './values'

const ns = 'Offerings.Details.form'

const Body = styled.div`
  background: white;
  border-radius: 0.3125rem;
  box-shadow: 0 0.1875rem 0.75rem 0 rgba(0, 0, 0, 0.14);
  padding: 1rem;
`

const Header = styled.header`
  display: flex;
  gap: 2rem;
  justify-content: space-between;
  padding-bottom: 1.125rem;

  @media ${Breakpoints.max.small} {
    align-items: flex-start;
    flex-direction: column;
    gap: 0.75rem;
  }
`

const Description = styled.div`
  ${Typography.Body4}
  color: ${designColors.neutral.veryDarkGray};

  @media ${Breakpoints.min.large} {
    ${Typography.Body2}
  }
`

const Totals = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  padding-top: 1rem;
`

const Total = styled.div`
  ${Typography.Body3}
  color: ${designColors.neutral.black};
`

const Footer = styled.footer`
  align-items: center;
  border-top: 0.0625rem solid ${designColors.neutral.mediumGray};
  display: flex;
  gap: 0.3125rem;
  margin-top: 1.125rem;
  padding-top: 0.625rem;

  @media ${Breakpoints.min.medium} {
    gap: 2.5rem;
  }

  @media ${Breakpoints.min.large} {
    gap: 4.375rem;
    padding-top: 1.125rem;
  }
`

const Terms = styled.div`
  ${Typography.Body4}
  color: ${designColors.neutral.darkGray};

  @media ${Breakpoints.min.large} {
    ${Typography.Body3}
  }
`

const FooterButtonWrapper = styled.div`
  flex: 0 0 content;

  > button {
    font-size: 0.875rem;
  }

  @media ${Breakpoints.min.large} {
    > button {
      font-size: 1rem;
      padding: 1rem 3rem;
    }
  }
`

export const OfferingForm = ({
  contract,
  currency = 'USD',
  offeringItems,
  offers,
  offering,
  requireSelectedQuote = true,
  selectedQuote = null,
}: {
  contract?: ContractDto | null
  currency?: string
  offeringItems: OfferingItem[]
  offers: OfferDto[]
  offering: OfferingDto
  /**
   * Legacy offerings page does not handle quote generation.
   * Until we retire that page, this prop handles the special case.
   */
  requireSelectedQuote?: boolean
  selectedQuote?: OrchestratedQuote | null
}) => {
  const { bidPrecision } = offering
  const isContract = !!contract
  const { breakpoint } = useDeviceInfo()

  const [enableValidation, setEnableValidation] = useState(false)
  const [enableEditing, setEnableEditing] = useState(false)

  const { initialValues, isEnabled, onSubmit } = useOfferingForm({
    offeringItems,
    offers,
    offering,
    contract,
    enableEditing,
    setEnableEditing,
  })

  // Render breakpoint-specific content client-side to avoid rehydration error.
  const [initialRender, setInitialRender] = useState(true)
  useEffect(() => setInitialRender(false), []) // oxlint-disable-line react/set-state-in-effect

  const showTotals = !isContract || SHOW_CONTRACT_QUANTITIES

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validateOnBlur={false}
      validateOnChange={enableValidation}
      validationSchema={useValidationSchema({
        bidPrecision,
        contract,
        offerAmountUnits: offering.offerAmountUnits,
      })}
    >
      {({ handleSubmit, isSubmitting, values, setValues }) => {
        // if contract only count quantity for rows that you made an offer on
        const totalQuantity = isContract
          ? values.itemRows.reduce<number>(
              (x, o) => x + (o.offerAmount ? o.item.quantity : 0),
              0
            )
          : values.itemRows.reduce<number>(
              (x, o) => x + (o.offerQuantity ?? 0),
              0
            )

        const quantityUnits = values.itemRows[0]?.item.quantityUnits ?? 'ITEM'

        const totalRetail = values.itemRows
          .map((o) =>
            getRetailQuantityPrice({
              offerAmountUnits: offering.offerAmountUnits,
              offerAmount: o.offerAmount,
              offerQuantity: o.offerQuantity,
              unitRetail: o.item.attributes.unitRetail,
              itemQuantity: o.item.quantity,
              contract,
            })
          )
          .reduce<number>((x, y) => x + y, 0)

        const showDescription = !isContract && isEnabled

        return (
          <Form
            id="OfferingForm"
            onKeyDown={(event) => {
              // Avoid accidental submission: must click CTA.
              if (event.key === 'Enter') event.preventDefault()
            }}
            onSubmit={(event) => {
              event.preventDefault()
              if (!isEnabled) return

              if (requireSelectedQuote && !selectedQuote) {
                showSnackbar({
                  content: (
                    <FormattedMessage id={`${ns}.errors.addressRequired`} />
                  ),
                  options: { type: 'error' },
                })
                return
              }

              setEnableValidation(true)
              handleSubmit(event)
            }}
          >
            <Body>
              <Header>
                {showDescription ? (
                  <Description>
                    <FormattedMessage id={`${ns}.description`} />
                  </Description>
                ) : (
                  // empty div to prevent layout shift of `Apply first Offer to All`
                  <div />
                )}
                {isEnabled && (
                  <>
                    {!isContract && (
                      <OfferAllQuantitiesButton
                        setValues={setValues}
                        values={values}
                      />
                    )}
                    <OfferAllButton setValues={setValues} values={values} />
                  </>
                )}
              </Header>
              {initialRender ? null : breakpoint === Breakpoint.SMALL ? (
                <OfferingFormTiles
                  bidPrecision={offering.bidPrecision}
                  contract={contract}
                  currency={currency}
                  rows={initialValues.itemRows}
                />
              ) : (
                <OfferingFormTable
                  bidPrecision={offering.bidPrecision}
                  contract={contract}
                  currency={currency}
                  rows={initialValues.itemRows}
                />
              )}
              {showTotals && (
                <Totals>
                  <Total>
                    <FormattedMessage
                      id={`${ns}.totalQuantity`}
                      values={{
                        x: <FormattedNumber value={totalQuantity} />,
                        units: isContract ? (
                          ''
                        ) : (
                          <FormattedMessage
                            id={`${ns}.quantityUnits.${quantityUnits}`}
                          />
                        ),
                      }}
                    />
                  </Total>
                  <Total>
                    <>
                      <FormattedMessage
                        id={`${ns}.totalAmount`}
                        values={{
                          x: (
                            <CurrencyStringCents
                              value={totalRetail}
                              currency={currency}
                            />
                          ),
                        }}
                      />
                      {isContract ? (
                        <>
                          {'/'}
                          <FormattedMessage id={`${ns}.order`} />
                        </>
                      ) : null}
                    </>
                  </Total>
                </Totals>
              )}
              <Footer>
                <Terms>
                  <FormattedMessage id={`${ns}.terms`} />
                </Terms>
                {isEnabled ? (
                  <FooterButtonWrapper>
                    {offers.length === 0 ? (
                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        appearance={isSubmitting ? 'loading' : 'primary'}
                      >
                        <FormattedMessage id={`${ns}.cta.makeAnOffer`} />
                      </Button>
                    ) : (
                      <>
                        {enableEditing ? (
                          <Button
                            type="submit"
                            disabled={isSubmitting}
                            appearance={isSubmitting ? 'loading' : 'primary'}
                          >
                            <FormattedMessage id={`${ns}.cta.confirmOffer`} />
                          </Button>
                        ) : (
                          <Button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault()
                              setEnableEditing(true)
                            }}
                          >
                            <FormattedMessage id={`${ns}.cta.updateOffer`} />
                          </Button>
                        )}
                      </>
                    )}
                  </FooterButtonWrapper>
                ) : null}
              </Footer>
            </Body>
            {initialRender ? null : <ErrorSnackbar ns={ns} />}
          </Form>
        )
      }}
    </Formik>
  )
}
